import { AxiosResponse, HttpStatusCode } from 'axios';
import { rawServerMessage, serverErrorMessage } from './serverErrorMessage';

export interface ApiErrorOptions {
  /** HTTP status of the failed response; undefined for network-level failures. */
  status?: number;
  /** Request URL of the failed response, used to derive a bounded route tag. */
  url?: string;
  /**
   * Marks an error the product expects and handles (e.g. a 409 "already
   * done" business conflict). The global error reporting skips these so they
   * don't file Sentry issues. Defaults to false.
   */
  expected?: boolean;
  /**
   * The response body's `message` exactly as sent, before sanitizing. Carries
   * the detail that makes a failure diagnosable — the Joi predicate, the field
   * name — which is precisely the text `message` must not contain. Reported to
   * Sentry as `extra`; never shown.
   */
  serverMessage?: string;
}

/**
 * Error thrown by unwrapApiResponse. Carries the HTTP status and request URL
 * of the failed response so the global reporter (reportApiError.ts) can tag a
 * normalized route and status — a generic message alone is not diagnosable.
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly url?: string;
  readonly expected: boolean;
  readonly serverMessage?: string;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.url = options.url;
    this.expected = options.expected ?? false;
    this.serverMessage = options.serverMessage;
  }
}

/**
 * AxiosWrapper never rejects: it resolves with the success response, the
 * error response (>=400), or null (network-level failure). Callers that just
 * optional-chain into `data` therefore can't tell a dead endpoint apart from
 * a genuinely empty payload. This helper draws that line: a 2xx response
 * carrying a `data` payload resolves; anything else throws an ApiError,
 * putting React Query into its error state (and reaching the global
 * QueryCache / MutationCache onError hooks, which report to Sentry).
 *
 * `data == null` (not `!data`): the backend's HttpResponse sets `data ??=
 * null` for bodyless successes, which should throw here, but scalar 0/false
 * payloads are legitimate data.
 *
 * Prefers the backend-provided `payload.message` over the fallback so users
 * see the real reason when the API supplies one — but through
 * serverErrorMessage, the same filter the toast path uses. `ApiError.message`
 * is user-visible (several call sites `toast.error(error.message)` it
 * directly), so reading `payload.message` raw here would have handed a driver
 * the Joi predicate no matter how carefully the toast path sanitized. The raw
 * text is kept on the error for Sentry.
 */
export const unwrapApiResponse = <T>(
  response: AxiosResponse | null,
  fallbackMsg: string
): T => {
  ensureApiSuccess(response, fallbackMsg);
  const payload = response?.data;
  const data = payload?.data;
  if (data == null) {
    throw new ApiError(serverErrorMessage(payload) ?? fallbackMsg, {
      status: response?.status,
      url: response?.config?.url,
      serverMessage: rawServerMessage(payload),
    });
  }
  return data as T;
};

/**
 * The status-only half of unwrapApiResponse: throws a tagged ApiError when the
 * response is a network failure (null) or an HTTP error (>=400), and returns
 * (void) on any 2xx — WITHOUT requiring a `data` payload. Use for endpoints
 * that legitimately 2xx with no body (e.g. a job-kickoff), where the full
 * unwrap would false-positive on the missing `data`.
 */
export const ensureApiSuccess = (
  response: AxiosResponse | null,
  fallbackMsg: string
): void => {
  const status = response?.status;
  if (!status || status >= HttpStatusCode.BadRequest) {
    throw new ApiError(serverErrorMessage(response?.data) ?? fallbackMsg, {
      status,
      url: response?.config?.url,
      serverMessage: rawServerMessage(response?.data),
    });
  }
};
